/**
 * Frontend mirror of api-server's resolveTripCurrency().
 *
 * Trip rows historically had two currency columns that could disagree:
 *   * local_currency_code   — populated 100% since 2016
 *   * billing_currency_code — added later, NULL on ~93% of pre-fix trips
 *
 * All UI fallbacks must resolve through this helper so the rule
 * (billing ?? local ?? "USD") stays consistent with the backend.
 */
import { COUNTRY_CURRENCIES } from "@workspace/countries";

export interface TripCurrencyShape {
  billing_currency_code?: string | null;
  local_currency_code?: string | null;
}

export const DEFAULT_TRIP_CURRENCY = "USD";

export function resolveTripCurrency(trip: TripCurrencyShape | null | undefined): string {
  const billing = (trip?.billing_currency_code || "").trim();
  if (billing) return billing.toUpperCase();
  const local = (trip?.local_currency_code || "").trim();
  if (local) return local.toUpperCase();
  return DEFAULT_TRIP_CURRENCY;
}

/**
 * Host-facing currency resolution.
 *
 * The canonical `resolveTripCurrency` is *billing-first* because that is
 * the currency the renter is actually charged in. On host surfaces
 * (owner booking list/detail, "Your earnings", payout forecasts) the
 * host thinks in — and is paid in — the car's LOCAL market currency, not
 * the renter's settlement currency. A ZAR car booked with a cross-border
 * partial payment carries `billing_currency_code = "USD"` while the
 * amounts shown to the host are denominated in ZAR; billing-first would
 * mislabel those amounts as USD.
 *
 * Resolution order (mirrors the request-card currency rule):
 *   local_currency_code
 *     → currencyForCountry(country_code)   (car's market country)
 *     → car_currency_code                  (cars.currency_code)
 *     → billing_currency_code              (last resort)
 *     → "USD"
 */
export interface HostBookingCurrencyShape {
  billing_currency_code?: string | null;
  local_currency_code?: string | null;
  /** The car's market country (e.g. `cars.country_code`). */
  country_code?: string | null;
  /** The car's stored market currency (e.g. `cars.currency_code`). */
  car_currency_code?: string | null;
}

export class UnresolvedHostBookingCurrencyError extends Error {
  readonly code = "UNRESOLVED_HOST_BOOKING_CURRENCY";
  constructor() {
    super("Host booking currency could not be resolved from any available signal.");
    this.name = "UnresolvedHostBookingCurrencyError";
  }
}

function norm(value: string | null | undefined): string {
  return (value || "").trim().toUpperCase();
}

function currencyForCountry(countryCode: string | null | undefined): string {
  const code = norm(countryCode);
  if (!code) return "";
  return norm(COUNTRY_CURRENCIES[code]);
}

/**
 * Ordered list of currency signals for a booking's LOCAL market
 * currency, most → least trustworthy. Shared by the host and the
 * renter-display resolvers (both are market-first) so every consumer
 * agrees on what "has any signal" means.
 */
function marketCurrencyCandidates(trip: HostBookingCurrencyShape | null | undefined): string[] {
  return [
    norm(trip?.local_currency_code),
    currencyForCountry(trip?.country_code),
    norm(trip?.car_currency_code),
    norm(trip?.billing_currency_code),
  ].filter(Boolean);
}

/** Lenient host resolver — falls back to USD when nothing resolves. */
export function resolveHostBookingCurrency(trip: HostBookingCurrencyShape | null | undefined): string {
  return marketCurrencyCandidates(trip)[0] || DEFAULT_TRIP_CURRENCY;
}

/**
 * Strict host resolver — throws `UnresolvedHostBookingCurrencyError`
 * when no currency signal exists, for surfaces where a silent `$`
 * fallback would be misleading (mirrors the web `resolveBookingCurrencyStrict`).
 */
export function resolveHostBookingCurrencyStrict(trip: HostBookingCurrencyShape | null | undefined): string {
  const resolved = marketCurrencyCandidates(trip)[0];
  if (!resolved) throw new UnresolvedHostBookingCurrencyError();
  return resolved;
}

/**
 * Task #5745 — renter *display* currency resolution.
 *
 * The canonical `resolveTripCurrency` is billing-first because that is
 * the currency the renter is actually CHARGED in (settlement). But every
 * renter-facing price *breakdown* (rental fee, delivery, service fee,
 * totals, "Paid now", "Total due to owner") is stored in the car's LOCAL
 * market currency: a New Zealand car booked cross-border carries
 * `local_currency_code = "NZD"` while `billing_currency_code = "USD"`,
 * so billing-first mislabels those NZD line items as USD.
 *
 * This resolver is market-first — identical ordering to the host
 * resolver (local → country → car → billing → USD) — because the
 * breakdown numbers are denominated the same way the host's are. Use it
 * ONLY for renter breakdown / quote surfaces. Settlement surfaces (the
 * Payments/transactions list, cancellation/refund math) MUST keep
 * `resolveTripCurrency` / `resolveBookingCurrencyStrict` so they keep
 * showing the real charge currency. This is a label change only — never
 * a re-conversion of the amounts.
 */
export type DisplayCurrencyShape = HostBookingCurrencyShape;

export class UnresolvedDisplayCurrencyError extends Error {
  readonly code = "UNRESOLVED_DISPLAY_CURRENCY";
  constructor() {
    super("Display currency could not be resolved from any available signal.");
    this.name = "UnresolvedDisplayCurrencyError";
  }
}

/** Lenient renter-display resolver — falls back to USD when nothing resolves. */
export function resolveDisplayCurrency(trip: DisplayCurrencyShape | null | undefined): string {
  return marketCurrencyCandidates(trip)[0] || DEFAULT_TRIP_CURRENCY;
}

/**
 * Strict renter-display resolver — throws `UnresolvedDisplayCurrencyError`
 * when no currency signal exists, for surfaces where a silent `$`
 * fallback would be misleading (mirrors `resolveBookingCurrencyStrict`
 * and `resolveHostBookingCurrencyStrict`).
 */
export function resolveDisplayCurrencyStrict(trip: DisplayCurrencyShape | null | undefined): string {
  const resolved = marketCurrencyCandidates(trip)[0];
  if (!resolved) throw new UnresolvedDisplayCurrencyError();
  return resolved;
}
